import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { cn } from '@workspace/ui/lib/utils';

import CloudflareResponseImage from '~/components/CloudflareResponsiveImage/CloudflareResponseImage';
import { defaultSrcSetParams } from '~/components/CloudflareResponsiveImage/constants';
import { CartSmallIcon, HeartIcon } from '~/components/MarketplaceIcons';
import { PriceText } from '~/components/Price/PriceText';
import { useProductCardV3 } from '~/components/ProductCardV3/hooks/useProductCardV3';
import { useProductListAnalyticsContext } from '~/components/ProductListAnalytics';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';
import { cartDrawerStore } from '~/pages/DubaiExpress/components/CartDrawerDE/cartDrawerStore';
import { cartStore, mainStore } from '~/stores';
import type { Product } from '~/stores/CategoriesStore/Product';
import { favoritesStore } from '~/stores/FavoritesStore/FavoritesStore';

interface Props {
  product: Product;
  /** Closes the popover before the cart drawer or a modal opens over it. */
  onClose: () => void;
}

const ROW_IMAGE_WIDTH = 120;

const ACTION_CLASS =
  'relative box-border flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-[8px] border-0 p-0 transition-colors duration-[120ms] ease-[cubic-bezier(.22,1,.36,1)]';

/**
 * Row of the header favorites popover: photo, name and price leading to the
 * product, a heart that removes the product (the row stays dimmed so it can
 * be returned) and a basket button that adds it or, once it is in the cart,
 * carries the quantity and opens the cart drawer.
 */
const HeaderDubaiExpressFavRowView = ({ product, onClose }: Props) => {
  const { t } = useTranslation();
  const { handleClickProduct } = useCategoryAnalytics();
  const {
    currentItem,
    image,
    currentPrice,
    mixAndMatch,
    cartItemsCount,
    isOutOfStock,
    isFavorite,
    productHasManyVariants,
    changeCount,
    openSelectSKUModal,
  } = useProductCardV3(product, undefined, 'm');
  const productList = useProductListAnalyticsContext();

  const isInCart = cartItemsCount > 0;
  const link = `/p/${product.slug}`;

  const handleFavorite = () => {
    favoritesStore.toggleFavorite(product, productList?.source ?? 'favorites');
    mainStore.sendToRN('hapticFeedback', { count: 1, type: 'soft' });
  };

  const handleCart = () => {
    if (isInCart) {
      onClose();
      cartDrawerStore.open(currentItem?.sku);
      return;
    }
    if (productHasManyVariants) {
      onClose();
      openSelectSKUModal();
      return;
    }
    changeCount(cartItemsCount + 1, 'add');
    if (
      currentItem &&
      cartStore.getCartItemCountById(currentItem) > cartItemsCount
    ) {
      onClose();
      cartDrawerStore.open(currentItem.sku);
    }
  };

  return (
    <div
      className={cn(
        'grid grid-cols-[56px_minmax(0,1fr)_auto] items-center gap-3 py-2 transition-opacity duration-200 ease-[cubic-bezier(.22,1,.36,1)]',
        !isFavorite && 'opacity-45',
      )}
    >
      <Link
        to={link}
        onClick={handleClickProduct(product)}
        aria-hidden
        tabIndex={-1}
        className="block size-14 overflow-hidden rounded-[10px] bg-white p-1"
      >
        {image && (
          <CloudflareResponseImage
            src={image}
            srcParams={{ width: ROW_IMAGE_WIDTH }}
            srcSetParams={defaultSrcSetParams}
            sizes="48px"
            alt=""
            loading="lazy"
            className="size-full object-contain"
          />
        )}
      </Link>
      <Link
        to={link}
        onClick={handleClickProduct(product)}
        className="no-underline! flex min-w-0 flex-col gap-0.5 text-[#141415]!"
      >
        <span className="line-clamp-2 text-[13px] leading-[18px]">
          {product.name}
        </span>
        <span className="font-semibold text-[14px] leading-[18px]">
          <PriceText>{mixAndMatch?.priceFrom ?? currentPrice}</PriceText>
        </span>
      </Link>
      <span className="flex items-center gap-1">
        <button
          type="button"
          aria-label={
            isFavorite ? t('favoritesDE:remove') : t('favoritesDE:restore')
          }
          onClick={handleFavorite}
          className={cn(
            ACTION_CLASS,
            'bg-transparent',
            isFavorite ? 'text-[#E94E1A]' : 'text-[#141415]',
          )}
        >
          <HeartIcon filled={isFavorite} className="size-5" />
        </button>
        {!isOutOfStock && (
          <button
            type="button"
            aria-label={
              isInCart
                ? t('cartDrawerDE:inCartCount', { count: cartItemsCount })
                : t('addToCart')
            }
            onClick={handleCart}
            className={cn(
              ACTION_CLASS,
              isInCart
                ? 'bg-[#E94E1A] text-white'
                : 'bg-[#F2F2F2] text-[#141415] hover:bg-[#E94E1A] hover:text-white',
            )}
          >
            {isInCart && (
              <span className="pointer-events-none absolute -top-2 -right-2 box-border flex h-5 min-w-5 items-center justify-center rounded-[10px] border border-white border-solid bg-[#141415] px-1 text-center font-semibold text-[10px] text-white leading-[18px]">
                {cartItemsCount}
              </span>
            )}
            <CartSmallIcon className="size-4" />
          </button>
        )}
      </span>
    </div>
  );
};

export const HeaderDubaiExpressFavRow = observer(HeaderDubaiExpressFavRowView);
